import React from "react";
import { ModalBuilder, Form, useFormField } from "@redriver/cinnamon";
import { useTranslation } from "react-i18next";
import { IconButton } from "features/../../../shared/components/buttons";
import { TranslatedModal } from "features/../../../shared/components/modals";
import Add from "features/../../../shared/assets/blue-add.svg";
import { LookupNames } from "./actions";
import {
  Actions,
  Targets,
} from "features/../../../shared/constants/permissions";
import { ImpersonatedUserPermissionCheck } from "modules/ImpersonatedUserPermissions";

const returnOnInvestmentAlreadySelected = (returnOnInvestments, id) =>
  (returnOnInvestments || []).some(
    (returnOnInvestment) =>
      `${returnOnInvestment.returnOnInvestmentId}` === `${id}`,
  );

const getMeteredServiceIds = (systemScheduleItems) => [
  ...new Set(
    (systemScheduleItems || [])
      .map((item) => item?.unit?.value)
      .filter(Boolean),
  ),
];

const AddReturnOnInvestment = ({ dealerOfficeId, disabled }) => {
  const { t } = useTranslation("systemSchedule");
  const [returnOnInvestments, setReturnOnInvestments] = useFormField(
    "returnOnInvestments",
  );
  const [systemScheduleItems] = useFormField("systemScheduleItems");

  const excludeReturnOnInvestmentIds = (returnOnInvestments || [])
    .map((returnOnInvestment) => returnOnInvestment.returnOnInvestmentId)
    .filter(Boolean);
  const meteredServiceIds = getMeteredServiceIds(systemScheduleItems);

  return (
    <ImpersonatedUserPermissionCheck
      target={Targets.SheetReturnOnInvestment}
      action={Actions.Create}
    >
      <ModalBuilder
        withForm
        initialData={{
          returnOnInvestmentId: null,
          returnOnInvestment: null,
          showInCustomerView: false,
        }}
        onSubmit={(formData) => {
          if (
            !formData.returnOnInvestmentId ||
            returnOnInvestmentAlreadySelected(
              returnOnInvestments,
              formData.returnOnInvestmentId,
            )
          ) {
            return;
          }

          setReturnOnInvestments([
            ...(returnOnInvestments || []),
            {
              id: `new_${Date.now()}_${formData.returnOnInvestmentId}`,
              returnOnInvestmentId: formData.returnOnInvestmentId,
              name:
                formData.returnOnInvestment?.name ||
                formData.returnOnInvestment?.text ||
                "",
              showInCustomerView: !!formData.showInCustomerView,
            },
          ]);
        }}
        renderTrigger={(showModal) => (
          <IconButton
            className="wizard-title-button"
            icon={Add}
            content={t("buttons.addReturnOnInvestment")}
            onClick={showModal}
            disabled={disabled || !dealerOfficeId}
          />
        )}
        renderModal={(modalProps, formProps) => (
          <TranslatedModal.Add
            {...modalProps}
            className="new-sch-item add-return-on-investment-modal"
            header={t("returnOnInvestmentModal.title")}
            submitDisabled={
              !formProps.value?.returnOnInvestmentId ||
              returnOnInvestmentAlreadySelected(
                returnOnInvestments,
                formProps.value?.returnOnInvestmentId,
              )
            }
          >
            <Form {...formProps} className="highlighted-inputs">
              <div className="sheet-step">
                <div className="schedule-step">
                  <Form.Dropdown
                    field="returnOnInvestmentId"
                    optionField="returnOnInvestment"
                    label={t("returnOnInvestmentModal.fields.roi.label")}
                    placeholder={t(
                      "returnOnInvestmentModal.fields.roi.placeholder",
                    )}
                    lookup={LookupNames.ReturnOnInvestments}
                    lookupParams={{
                      dealerOfficeId,
                      excludeReturnOnInvestmentIds,
                      filterOnMeteredServices: true,
                      meteredServiceIds,
                    }}
                    required
                    inline
                    search
                  />

                  <Form.Trigger
                    field="returnOnInvestmentId"
                    event="onChange"
                    action="change"
                    target="showInCustomerView"
                    value={({ fields }) =>
                      fields.returnOnInvestment?.defaultOnCustomerView || false
                    }
                  />

                  <Form.Checkbox
                    field="showInCustomerView"
                    label={t(
                      "returnOnInvestmentModal.fields.customerView.label",
                    )}
                  />
                </div>
              </div>
            </Form>
          </TranslatedModal.Add>
        )}
      />
    </ImpersonatedUserPermissionCheck>
  );
};

export default AddReturnOnInvestment;
